import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType, SwiperOptions } from 'swiper/types';
import { A11y, Pagination, Mousewheel } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { Paragraph } from 'components/src/typography';
import { Maybe } from 'types/generated';
import { useState } from 'react';
import clsx from 'clsx';

const DEFAULT_BREAKPOINTS = {
  544: {
    slidesPerView: 2,
    spaceBetween: 16,
  },
  768: {
    slidesPerView: 2.5,
    spaceBetween: 20,
  },
  1024: {
    slidesPerView: 4,
    spaceBetween: 24,
  },
  1280: {
    slidesPerView: 4,
    spaceBetween: 24,
  },
};

type Props = {
  cards: Array<React.ReactNode>;
  disclaimer?: Maybe<string>;
  breakpoints?: {
    [width: number]: SwiperOptions;
  };
  showShadows?: boolean;
  backgroundColor?: string;
  isMobile?: boolean;
};

const SynCardCarousel = ({
  cards,
  breakpoints = DEFAULT_BREAKPOINTS,
  disclaimer,
  showShadows = false,
  backgroundColor = 'white-15',
  isMobile = false,
}: Props) => {
  const [showRightShadow, setShowRightShadow] = useState(false);
  const [showLeftShadow, setShowLeftShadow] = useState(false);

  const handleSetShadow = (e: SwiperType) => {
    setShowLeftShadow(e.isEnd && e.pagination.bullets.length > 1);
    setShowRightShadow(!e.isEnd && e.pagination.bullets.length > 1);
  };

  return (
    <div className="relative col-span-full">
      {showShadows && !isMobile && showLeftShadow && (
        <div
          className={`absolute left-0 top-0 z-10 h-full w-24 bg-gradient-to-r from-${backgroundColor} to-transparent`}
        />
      )}
      {showShadows && !isMobile && showRightShadow && (
        <div
          className={`absolute right-0 top-0 z-10 h-full w-24 bg-gradient-to-l from-${backgroundColor} to-transparent`}
        />
      )}
      <Swiper
        modules={[Pagination, A11y, Mousewheel]}
        pagination={{ clickable: true }}
        mousewheel={{ forceToAxis: true }}
        breakpoints={breakpoints}
        className="!flex !flex-col"
        onSlideChange={handleSetShadow}
        onInit={handleSetShadow}
        onResize={handleSetShadow}
        spaceBetween={20}
        slidesPerView={1.2}
      >
        {cards.map((card, index) => (
          <SwiperSlide
            key={index}
            style={{ display: 'flex', height: 'auto' }}
            className={clsx(`responsive-slide my-64 mt-16 rounded-28`, {
              'mr-12': index === cards.length - 1,
            })}
          >
            {card}
          </SwiperSlide>
        ))}

        {disclaimer && (
          <div className={`mt-24 rounded-28 ${backgroundColor} p-8 lg:p-16`}>
            <Paragraph classes="inline text-red-100" variant="small">
              {'*'}
            </Paragraph>
            <Paragraph variant="small" classes="inline text-black-100">
              {disclaimer}
            </Paragraph>
          </div>
        )}
      </Swiper>
    </div>
  );
};

export default SynCardCarousel;
